import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { calculateLineMaxAmount } from './calculateProductMaxAmount';

const STORAGE_KEY = 'membrz:cart';

export const sameCartProduct = (firstProduct, secondProduct) =>
  firstProduct.id === secondProduct.id &&
  (firstProduct.event_product_listing_id || null) ===
    (secondProduct.event_product_listing_id || null);

// A cart line is one (product, listing, variant) — two sizes of the same tee
// are two lines.
export const sameCartLine = (item, product, variantId) =>
  sameCartProduct(item.product, product) &&
  (item.variant?.id || null) === (variantId || null);

// Quantity of a product held across the cart's OTHER lines (the line
// identified by excludeVariantId is skipped). The redeem limit is
// product-level, so SKU lines of one product share its allowance.
export const amountOfProductInCart = (cartItems, product, excludeVariantId = null) =>
  cartItems.reduce((sum, item) => {
    if (item.product.id !== product.id) return sum;
    if ((item.variant?.id || null) === (excludeVariantId || null)) return sum;
    return sum + item.amount;
  }, 0);

const readStoredCart = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.cartItems)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const initial = useRef(null);
  if (initial.current === null) initial.current = readStoredCart() || {};

  const [cartItems, setCartItems] = useState(initial.current.cartItems || []);
  const [cartEventId, setCartEventId] = useState(initial.current.cartEventId || null);

  useEffect(() => {
    if (cartItems.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ cartItems, cartEventId }));
  }, [cartItems, cartEventId]);

  const addToCart = useCallback((product, variant = null) => {
    const productEventId = product.event_id;

    if (cartItems.length > 0 && (productEventId || null) !== (cartEventId || null)) {
      return { ok: false, reason: 'event_mismatch' };
    }

    if ((product.variants?.length > 0) && !variant) {
      return { ok: false, reason: 'variant_required' };
    }

    // The product's redeem allowance is shared across its SKU lines: a new
    // line is refused outright when other lines already consume it all.
    const heldElsewhere = amountOfProductInCart(cartItems, product, variant?.id);
    const existingLine = cartItems.find((item) => sameCartLine(item, product, variant?.id));
    if (!existingLine && calculateLineMaxAmount(product, variant, heldElsewhere) < 1) {
      return { ok: false, reason: 'redeem_limit_reached' };
    }

    if (!cartEventId) {
      setCartEventId(productEventId);
    }

    setCartItems((prev) => {
      const existing = prev.find((item) => sameCartLine(item, product, variant?.id));
      const others = amountOfProductInCart(prev, product, variant?.id);
      const maxAmount = calculateLineMaxAmount(product, variant, others);
      if (existing) {
        if (existing.amount >= maxAmount) return prev;
        return prev.map((item) =>
          sameCartLine(item, product, variant?.id)
            ? { ...item, amount: item.amount + 1 }
            : item
        );
      }
      if (maxAmount < 1) return prev;
      return [...prev, { product, amount: 1, ...(variant && { variant }) }];
    });
    return { ok: true };
  }, [cartEventId, cartItems]);

  const syncProducts = useCallback((products) => {
    if (!products || products.length === 0) return;
    setCartItems((prev) => {
      // Lines are clamped sequentially so all SKU lines of one product stay
      // jointly within its redeem allowance after a refresh.
      const claimedPerProduct = {};
      const next = [];
      prev.forEach((item) => {
        const fresh = products.find((product) => sameCartProduct(product, item.product));
        if (!fresh) {
          next.push(item);
          return;
        }
        // Re-resolve the line's variant on the fresh product. A line without a
        // variant whose product has since gained variants (stale pre-deploy
        // cart) or whose SKU is gone can no longer be purchased — drop it.
        let freshVariant = null;
        if (item.variant) {
          freshVariant = (fresh.variants || []).find((variant) => variant.id === item.variant.id);
          if (!freshVariant) return;
        } else if (fresh.variants?.length > 0) {
          return;
        }
        const claimed = claimedPerProduct[fresh.id] || 0;
        const maxAmount = calculateLineMaxAmount(fresh, freshVariant, claimed);
        if (maxAmount <= 0) return;
        const amount = Math.min(item.amount, maxAmount);
        claimedPerProduct[fresh.id] = claimed + amount;
        next.push({
          product: fresh,
          amount,
          ...(freshVariant && { variant: freshVariant }),
        });
      });
      if (next.length === 0) setCartEventId(null);
      return next;
    });
  }, []);

  const removeFromCart = useCallback((productId, variantId = null) => {
    setCartItems((prev) => {
      const next = prev.filter(
        (item) => item.product.id !== productId || (item.variant?.id || null) !== (variantId || null)
      );
      if (next.length === 0) setCartEventId(null);
      return next;
    });
  }, []);

  const updateQuantity = useCallback((productId, newAmount, variantId = null) => {
    if (newAmount < 1) {
      removeFromCart(productId, variantId);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) => {
        if (item.product.id !== productId || (item.variant?.id || null) !== (variantId || null)) return item;
        const others = amountOfProductInCart(prev, item.product, item.variant?.id || null);
        const maxAmount = calculateLineMaxAmount(item.product, item.variant, others);
        return { ...item, amount: Math.min(newAmount, maxAmount) };
      })
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setCartEventId(null);
  }, []);

  const cartTotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const price = item.product.display_price || 0;
      return sum + price * item.amount;
    }, 0);
  }, [cartItems]);

  const cartItemCount = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.amount, 0);
  }, [cartItems]);

  const value = useMemo(() => ({
    cartItems,
    cartEventId,
    cartTotal,
    cartItemCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    syncProducts,
  }), [cartItems, cartEventId, cartTotal, cartItemCount, addToCart, removeFromCart, updateQuantity, clearCart, syncProducts]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
